/** @file gizmoLines: a tool's gizmo handles placed in the world at a model's origin, axes and size. */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Tool } from '../../state/editorTypes';
import type { GizmoModel } from './gizmoModel';
import type { GizmoShape } from './gizmoShape';
import { moveGizmo } from './moveGizmo';
import { planeSigns } from './planeSigns';
import { rotateGizmo } from './rotateGizmo';
import { scaleGizmo } from './scaleGizmo';
import { silhouetteOf } from './silhouetteOf';

/** The shapes per tool (the move planes take the viewer's side); hand and IK draw no gizmo (IK drags the joint). */
const SHAPES: Record<Tool, (model: GizmoModel, view: View) => GizmoShape[]> = {
  hand: () => [], move: (m, v) => moveGizmo(planeSigns(m.axes, v)), rotate: (_, v) => rotateGizmo(silhouetteOf(v)), scale: scaleGizmo, ik: () => [],
};

/** A world map `origin + size (u0 e0 + u1 e1 + u2 e2)`. */
const placer = (o: Vec3, size: number, [a, b, c]: readonly [Vec3, Vec3, Vec3]) => (p: Vec3): Vec3 => [
  o[0] + size * (p[0] * a[0] + p[1] * b[0] + p[2] * c[0]),
  o[1] + size * (p[0] * a[1] + p[1] * b[1] + p[2] * c[1]),
  o[2] + size * (p[0] * a[2] + p[1] * b[2] + p[2] * c[2]),
];

/**
 * Each handle's polylines and quads in world space: `frame: 'axes'` points along the model's axes, `frame: 'screen'`
 * points along the view's right and up taken back from display space (z / `zScaleView`), so they project onto the
 * view plane exactly as the drawn screen-aligned meshes do.
 */
export function gizmoLines(tool: Tool, model: GizmoModel, size: number, view: View): GizmoShape[] {
  const { right, up, back } = view.basis;
  const z = view.zScaleView;
  const toAxes = placer(model.origin, size, model.axes);
  const toScreen = placer(model.origin, size, [[right[0], right[1], right[2] / z], [up[0], up[1], up[2] / z], [back[0], back[1], back[2] / z]]);
  return SHAPES[tool](model, view).map((s) => {
    const place = s.frame === 'axes' ? toAxes : toScreen;
    return { ...s, lines: s.lines.map((l) => l.map(place)), quads: s.quads.map((q) => q.map(place)) };
  });
}
